// Posting a remittance onto a run's claim lines.
//
// Moved out of pages/finance/BillingRun.js. The decision of what each claim
// pays and which line it belongs to is matchRemittance() in
// lib/edi/remitPosting.js, shared by the RA-PDF and 835 paths so the two can
// never post the same remittance differently. Only the writing happens here.
// Takes the Supabase client as its first argument so tests can pin it.

import { matchRemittance, runIsSettled, runPaidFromLines } from '../edi/remitPosting.js';
import { must } from './must.js';

/**
 * Record a remittance, pay the lines it matches, and re-total the run.
 *
 * `existingId` is the row the SFTP worker already wrote when it fetched an
 * 835. Posting that one has to UPDATE it, not insert a second: two rows for
 * one payment is how a remittance gets posted twice.
 *
 * @param {object[]} p.claims      claims in the shared shape (parse835, or claimFromRaRow for a PDF)
 * @param {object}   p.remittance  the receipt's own columns (source, trace, amounts, payer, file)
 * @returns {{ matched, unmatched, uncertain, remittanceId }}
 */
export const postRemittance = async (sb, { billingRunId, claims, remittance, profile, existingId = null, now }) => {
  // Read before anything is written: a failed read used to record the
  // receipt with every claim unmatched.
  const lines = await must(sb.from('claim_lines').select('*').eq('billing_run_id', billingRunId), "load the run's lines");
  const { matched, unmatched, uncertain } = matchRemittance({ claims, lines: lines || [] });

  // The receipt goes in first, so every line it pays can point back at it
  // and a payment can never be posted twice from two sources with nothing
  // to show it already happened once.
  const receipt = {
    ...remittance,
    billing_run_id: billingRunId,
    imported_by: profile?.id || null,
    imported_by_name: profile?.full_name || '',
    lines_matched: matched.length,
    lines_unmatched: unmatched.length,
    lines_uncertain: uncertain.length,
    posted_at: now,
  };
  const { data: remit, error: remitErr } = existingId
    ? await sb.from('remittances').update(receipt).eq('id', existingId).select('id').single()
    : await sb.from('remittances').insert(receipt).select('id').single();
  if (remitErr) throw new Error(`Could not record the remittance: ${remitErr.message}`);

  const failed = [];
  const failedLines = new Set();
  for (const m of matched) {
    const { error } = await sb.from('claim_lines')
      .update({ ...m.update, remittance_id: remit.id, updated_at: now })
      .eq('id', m.line.id);
    if (error) { failed.push({ ...m.claim, _error: error.message }); failedLines.add(m.line.id); }
  }

  // By line, not ICN: claims without an ICN all compared equal, so one failed
  // line used to drop every ICN-less match from what was applied.
  const applied = matched.filter(m => !failedLines.has(m.line.id));
  // Every remittance this run has had, not just this one: summed from the
  // lines, which now carry this payment too.
  const paidLines = await must(sb.from('claim_lines').select('paid_amount').eq('billing_run_id', billingRunId),
    "re-read the run's payments (the lines were paid)");
  await must(sb.from('billing_runs').update({
    total_paid_amount: runPaidFromLines(paidLines || []),
    ...(remittance.ra_number ? { ra_reference: remittance.ra_number } : {}),
    ...(remittance.file_url ? { ra_pdf_url: remittance.file_url } : {}),
    ...(runIsSettled(lines || [], applied) ? { status: 'closed' } : {}),
  }).eq('id', billingRunId), "update the run's paid total (the lines were paid)");

  return { matched: applied, unmatched: [...unmatched, ...failed], uncertain, remittanceId: remit.id };
};
